(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-myron-creator-bonus]';
  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  function queryAll(scope, selector) {
    return Array.prototype.slice.call(scope.querySelectorAll(selector));
  }

  function isReducedMotion() {
    return typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION_QUERY).matches;
  }

  function addListener(cleanups, element, eventName, listener, options) {
    if (!element || !element.addEventListener) return;
    element.addEventListener(eventName, listener, options);
    cleanups.push(function () {
      element.removeEventListener(eventName, listener, options);
    });
  }

  function pauseVideos(scope) {
    queryAll(scope, 'video').forEach(function (video) {
      if (!video.paused) video.pause();
    });
  }

  function isMediaEligible(media) {
    var visibilityPanel = media.closest('[data-creator-visibility-panel]');
    if (visibilityPanel) {
      var visibilityLab = visibilityPanel.closest('[data-creator-visibility]');
      if (visibilityLab && visibilityLab.dataset.visibilityState !== visibilityPanel.dataset.visibilityPanel) return false;
    }

    var examplePanel = media.closest('[data-creator-example-panel]');
    if (examplePanel && examplePanel.hidden) return false;

    return true;
  }

  function initLoopMedia(root, cleanups) {
    var states = [];
    var observer = null;
    var activeState = null;

    queryAll(root, '[data-creator-loop-media]').forEach(function (media) {
      var video = media.querySelector('video');
      if (!video) return;

      var playButton = media.querySelector('[data-creator-video-play]');
      var state = { media: media, video: video, playButton: playButton, visible: false };
      states.push(state);

      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('preload', 'metadata');

      function updateButton() {
        if (!state.playButton) return;
        state.playButton.hidden = !video.paused && !video.ended;
      }

      function pauseState() {
        if (!video.paused) video.pause();
        if (activeState === state) activeState = null;
        updateButton();
      }

      function playState(manual) {
        if (!manual && (!state.visible || !isMediaEligible(media) || isReducedMotion())) {
          updateButton();
          return;
        }

        if (activeState && activeState !== state) {
          activeState.video.pause();
        }

        video.preload = 'metadata';
        var playback = video.play();
        if (playback && typeof playback.then === 'function') {
          playback.then(function () {
            activeState = state;
            updateButton();
          }).catch(function () {
            updateButton();
          });
        } else if (!video.paused) {
          activeState = state;
          updateButton();
        }
      }

      state.play = playState;
      state.pause = pauseState;
      state.updateButton = updateButton;

      addListener(cleanups, video, 'play', function () {
        activeState = state;
        updateButton();
      });
      addListener(cleanups, video, 'pause', updateButton);
      addListener(cleanups, video, 'ended', updateButton);
      addListener(cleanups, video, 'error', function () {
        if (state.playButton) state.playButton.hidden = false;
      });

      if (playButton) {
        addListener(cleanups, playButton, 'click', function () {
          playState(true);
        });
      }

      updateButton();
    });

    function syncMedia() {
      states.forEach(function (state) {
        var eligible = state.visible && isMediaEligible(state.media);
        if (!eligible) {
          state.video.preload = 'none';
          state.pause();
        } else {
          state.video.preload = 'metadata';
          state.play(false);
        }
      });
    }

    if (typeof window.IntersectionObserver === 'function') {
      observer = new window.IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var state = states.filter(function (candidate) {
            return candidate.media === entry.target;
          })[0];
          if (!state) return;
          state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.45;
        });
        syncMedia();
      }, { threshold: [0, 0.45, 0.75] });

      states.forEach(function (state) {
        observer.observe(state.media);
      });

      cleanups.push(function () {
        observer.disconnect();
      });
    } else {
      states.forEach(function (state, index) {
        state.visible = index === 0;
      });
      syncMedia();
    }

    if (typeof window.matchMedia === 'function') {
      var motionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
      var onMotionChange = function () {
        if (motionQuery.matches) {
          states.forEach(function (state) {
            state.pause();
          });
        } else {
          syncMedia();
        }
      };

      if (motionQuery.addEventListener) {
        motionQuery.addEventListener('change', onMotionChange);
        cleanups.push(function () {
          motionQuery.removeEventListener('change', onMotionChange);
        });
      } else if (motionQuery.addListener) {
        motionQuery.addListener(onMotionChange);
        cleanups.push(function () {
          motionQuery.removeListener(onMotionChange);
        });
      }
    }

    root._myronCreatorMediaSync = syncMedia;
    root._myronCreatorPauseVideos = function (scope) {
      pauseVideos(scope || root);
    };
  }

  function initChapterNav(root, cleanups) {
    var nav = root.querySelector('[data-creator-nav]');
    var indicator = root.querySelector('[data-creator-nav-indicator]');
    if (!nav || !indicator) return;

    var links = queryAll(nav, '[data-creator-nav-link]');
    var chapters = links.map(function (link) {
      return root.querySelector(link.getAttribute('href'));
    }).filter(Boolean);
    var activeLink = null;
    var indicatorFrame = null;
    var chapterFrame = null;

    function clamp(value, minimum, maximum) {
      return Math.min(Math.max(value, minimum), maximum);
    }

    function scheduleIndicatorUpdate() {
      if (indicatorFrame !== null) return;

      var render = function () {
        var link = activeLink || links[0];
        indicatorFrame = null;
        if (!link) return;

        var linkRect = link.getBoundingClientRect();
        var navRect = nav.getBoundingClientRect();
        var offset = linkRect.left - navRect.left + nav.scrollLeft;

        indicator.style.width = linkRect.width + 'px';
        indicator.style.transform = 'translate3d(' + offset + 'px, 0, 0)';
      };

      if (typeof window.requestAnimationFrame === 'function') {
        indicatorFrame = window.requestAnimationFrame(render);
      } else {
        render();
      }
    }

    function scrollActiveLinkIntoView(link) {
      if (!link) return;

      var linkRect = link.getBoundingClientRect();
      var navRect = nav.getBoundingClientRect();
      var edgeGap = 16;
      var targetScrollLeft = nav.scrollLeft;

      if (linkRect.left < navRect.left) {
        targetScrollLeft += linkRect.left - navRect.left - edgeGap;
      } else if (linkRect.right > navRect.right) {
        targetScrollLeft += linkRect.right - navRect.right + edgeGap;
      } else {
        return;
      }

      targetScrollLeft = clamp(targetScrollLeft, 0, Math.max(0, nav.scrollWidth - nav.clientWidth));
      if (Math.abs(targetScrollLeft - nav.scrollLeft) < 1) return;

      if (typeof nav.scrollTo === 'function') {
        nav.scrollTo({
          left: targetScrollLeft,
          behavior: isReducedMotion() ? 'auto' : 'smooth'
        });
      } else {
        nav.scrollLeft = targetScrollLeft;
      }
    }

    function setActive(link, shouldScrollNav) {
      if (!link) return;

      var changed = activeLink !== link;
      activeLink = link;

      links.forEach(function (candidate) {
        if (candidate === link) {
          candidate.setAttribute('aria-current', 'location');
        } else {
          candidate.removeAttribute('aria-current');
        }
      });

      scheduleIndicatorUpdate();

      if (shouldScrollNav && changed) {
        scrollActiveLinkIntoView(link);
      }
    }

    function linkForChapter(chapter) {
      return links.filter(function (link) {
        return link.getAttribute('href') === '#' + chapter.id;
      })[0];
    }

    function getActivationOffset() {
      return Math.max(0, nav.getBoundingClientRect().bottom + 12);
    }

    function isAtPageEnd() {
      var documentHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body ? document.body.scrollHeight : 0
      );
      return window.pageYOffset + window.innerHeight >= documentHeight - 2;
    }

    function updateActiveChapter() {
      chapterFrame = null;
      if (!chapters.length) return;

      var activationOffset = getActivationOffset();
      var activeChapter = chapters[0];

      chapters.forEach(function (chapter) {
        if (chapter.getBoundingClientRect().top <= activationOffset) {
          activeChapter = chapter;
        }
      });

      if (isAtPageEnd()) activeChapter = chapters[chapters.length - 1];
      setActive(linkForChapter(activeChapter), true);
    }

    function scheduleActiveChapterUpdate() {
      if (chapterFrame !== null) return;

      if (typeof window.requestAnimationFrame === 'function') {
        chapterFrame = window.requestAnimationFrame(updateActiveChapter);
      } else {
        updateActiveChapter();
      }
    }

    links.forEach(function (link) {
      addListener(cleanups, link, 'click', function (event) {
        var chapter = root.querySelector(link.getAttribute('href'));
        if (!chapter) return;
        event.preventDefault();
        setActive(link, true);
        chapter.scrollIntoView({ behavior: isReducedMotion() ? 'auto' : 'smooth', block: 'start' });
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', link.getAttribute('href'));
        }
      });
    });

    addListener(cleanups, nav, 'scroll', function () {
      scheduleIndicatorUpdate();
    }, { passive: true });

    addListener(cleanups, window, 'scroll', scheduleActiveChapterUpdate, { passive: true });

    addListener(cleanups, window, 'resize', function () {
      scheduleIndicatorUpdate();
      scrollActiveLinkIntoView(activeLink || links[0]);
      scheduleActiveChapterUpdate();
    });

    cleanups.push(function () {
      if (indicatorFrame !== null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(indicatorFrame);
      }
      if (chapterFrame !== null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(chapterFrame);
      }
    });

    var initialLink = links.filter(function (link) {
      return link.getAttribute('href') === window.location.hash;
    })[0] || links[0];

    setActive(initialLink, false);
    scheduleActiveChapterUpdate();
  }

  function initFrameLab(root, cleanups) {
    var lab = root.querySelector('[data-creator-frame-lab]');
    if (!lab) return;

    var buttons = queryAll(lab, '[data-creator-frame-control]');
    var panels = queryAll(lab, '[data-frame-media]');
    var label = lab.querySelector('[data-creator-frame-message-label]');
    var message = lab.querySelector('[data-creator-frame-message-text]');

    function setFrameState(state) {
      var activeButton = null;
      lab.dataset.frameState = state;

      buttons.forEach(function (button) {
        var isActive = button.dataset.frameState === state;
        button.setAttribute('aria-pressed', String(isActive));
        if (isActive) activeButton = button;
      });

      panels.forEach(function (panel) {
        panel.hidden = panel.dataset.frameMedia !== state;
      });

      if (activeButton && label) label.textContent = activeButton.dataset.frameLabel || activeButton.textContent;
      if (activeButton && message && activeButton.dataset.frameMessage) {
        message.textContent = activeButton.dataset.frameMessage;
      }
    }

    buttons.forEach(function (button) {
      addListener(cleanups, button, 'click', function () {
        setFrameState(button.dataset.frameState);
      });
    });

    setFrameState(lab.dataset.frameState || 'ideal');
  }

  function initGuideControls(root, cleanups) {
    queryAll(root, '[data-creator-safe-zone]').forEach(function (lab) {
      queryAll(lab, '[data-creator-guide-button]').forEach(function (button) {
        addListener(cleanups, button, 'click', function () {
          var guide = button.dataset.guide;
          var nextPressed = button.getAttribute('aria-pressed') !== 'true';
          button.setAttribute('aria-pressed', String(nextPressed));
          lab.dataset['guide' + guide.charAt(0).toUpperCase() + guide.slice(1)] = String(nextPressed);
        });
      });
    });
  }


  function initCapCutSliders(root, cleanups) {
    var sliderStates = [];

    queryAll(root, '[data-capcut-slider]').forEach(function (slider) {
      var slides = queryAll(slider, '[data-capcut-slide]');
      var dots = queryAll(slider, '[data-capcut-dot]');
      var previousButton = slider.querySelector('[data-capcut-previous]');
      var nextButton = slider.querySelector('[data-capcut-next]');
      var counter = slider.querySelector('[data-capcut-slider-counter]');
      var viewport = slider.querySelector('[data-capcut-slider-viewport]');
      var activeIndex = 0;
      var touchStart = null;

      if (!slides.length || !previousButton || !nextButton || !viewport) return;

      function formatStep(value) {
        return String(value).padStart(2, '0');
      }

      function setSlide(index) {
        activeIndex = Math.max(0, Math.min(index, slides.length - 1));

        slides.forEach(function (slide, slideIndex) {
          slide.hidden = slideIndex !== activeIndex;
        });

        dots.forEach(function (dot, dotIndex) {
          if (dotIndex === activeIndex) {
            dot.setAttribute('aria-current', 'step');
          } else {
            dot.removeAttribute('aria-current');
          }
        });

        previousButton.disabled = activeIndex === 0;
        nextButton.disabled = activeIndex === slides.length - 1;

        if (counter) {
          counter.textContent = formatStep(activeIndex + 1) + ' / ' + formatStep(slides.length);
        }
      }

      addListener(cleanups, previousButton, 'click', function () {
        setSlide(activeIndex - 1);
      });

      addListener(cleanups, nextButton, 'click', function () {
        setSlide(activeIndex + 1);
      });

      dots.forEach(function (dot) {
        addListener(cleanups, dot, 'click', function () {
          setSlide(Number(dot.dataset.capcutIndex));
        });
      });

      addListener(cleanups, viewport, 'pointerdown', function (event) {
        if (event.pointerType && event.pointerType !== 'touch') return;

        touchStart = {
          pointerId: event.pointerId,
          x: event.clientX,
          y: event.clientY
        };
      });

      addListener(cleanups, viewport, 'pointerup', function (event) {
        if (!touchStart || event.pointerId !== touchStart.pointerId) return;

        var deltaX = event.clientX - touchStart.x;
        var deltaY = event.clientY - touchStart.y;
        touchStart = null;

        if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        setSlide(activeIndex + (deltaX < 0 ? 1 : -1));
      });

      addListener(cleanups, viewport, 'pointercancel', function () {
        touchStart = null;
      });

      setSlide(0);
      sliderStates.push({ slides: slides, setSlide: setSlide });
    });

    if (!sliderStates.length) return;

    addListener(cleanups, document, 'shopify:block:select', function (event) {
      var selectedSlide = null;
      var selectedBlockId = event.detail && event.detail.blockId;

      if (event.target && event.target.closest) {
        selectedSlide = event.target.closest('[data-capcut-slide]');
      }

      if ((!selectedSlide || !root.contains(selectedSlide)) && selectedBlockId) {
        sliderStates.forEach(function (state) {
          state.slides.forEach(function (slide) {
            if (slide.getAttribute('data-shopify-editor-block') === selectedBlockId) {
              selectedSlide = slide;
            }
          });
        });
      }

      if (!selectedSlide || !root.contains(selectedSlide)) return;

      sliderStates.forEach(function (state) {
        var selectedIndex = state.slides.indexOf(selectedSlide);
        if (selectedIndex !== -1) state.setSlide(selectedIndex);
      });
    });
  }

  function initVisibilityLab(root, cleanups) {
    queryAll(root, '[data-creator-visibility]').forEach(function (lab) {
      var visibilityButtons = queryAll(lab, '[data-creator-visibility-control]');
      var visibilityPanels = queryAll(lab, '[data-creator-visibility-panel]');

      function setVisibilityState(state) {
        lab.dataset.visibilityState = state;

        visibilityButtons.forEach(function (button) {
          button.setAttribute('aria-pressed', String(button.dataset.visibilityState === state));
        });

        visibilityPanels.forEach(function (panel) {
          panel.hidden = panel.dataset.visibilityPanel !== state;
        });

        if (root._myronCreatorMediaSync) root._myronCreatorMediaSync();
      }

      visibilityButtons.forEach(function (button) {
        addListener(cleanups, button, 'click', function () {
          setVisibilityState(button.dataset.visibilityState);
        });
      });

      setVisibilityState(lab.dataset.visibilityState || 'clear');
    });
  }

  function initExamples(root, cleanups) {
    queryAll(root, '[data-creator-example]').forEach(function (example) {
      var buttons = queryAll(example, '[data-creator-example-control]');
      var panels = queryAll(example, '[data-creator-example-panel]');

      function setView(view) {
        example.dataset.exampleView = view;
        buttons.forEach(function (button) {
          button.setAttribute('aria-pressed', String(button.dataset.exampleView === view));
        });
        panels.forEach(function (panel) {
          panel.hidden = window.innerWidth <= 768 && panel.dataset.examplePanel !== view;
        });
        if (root._myronCreatorMediaSync) root._myronCreatorMediaSync();
      }

      buttons.forEach(function (button) {
        addListener(cleanups, button, 'click', function () {
          setView(button.dataset.exampleView);
        });
      });

      addListener(cleanups, window, 'resize', function () {
        setView(example.dataset.exampleView || 'bad');
      });

      setView(example.dataset.exampleView || 'bad');
    });
  }

  function initChecklist(root, cleanups) {
    queryAll(root, '[data-creator-checklist-item]').forEach(function (item) {
      addListener(cleanups, item, 'click', function () {
        item.setAttribute('aria-pressed', String(item.getAttribute('aria-pressed') !== 'true'));
      });
    });
  }

  function destroy(root) {
    if (!root || !root._myronCreatorCleanup) return;
    root._myronCreatorCleanup.forEach(function (cleanup) {
      cleanup();
    });
    root._myronCreatorCleanup = null;
    delete root.dataset.myronCreatorInitialized;
  }

  function init(root) {
    if (!root || root.dataset.myronCreatorInitialized === 'true') return;

    root.dataset.myronCreatorInitialized = 'true';
    var cleanups = [];
    root._myronCreatorCleanup = cleanups;

    initLoopMedia(root, cleanups);
    initChapterNav(root, cleanups);
    initFrameLab(root, cleanups);
    initGuideControls(root, cleanups);
    initCapCutSliders(root, cleanups);
    initVisibilityLab(root, cleanups);
    initExamples(root, cleanups);
    initChecklist(root, cleanups);
  }

  function initAll(scope) {
    queryAll(scope || document, ROOT_SELECTOR).forEach(init);
  }

  function boot() {
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    queryAll(event.target, ROOT_SELECTOR).forEach(destroy);
    if (event.target && event.target.matches && event.target.matches(ROOT_SELECTOR)) destroy(event.target);
  });
}());